import {Alert, Card, FormField, Separator, Switch, type FormControl} from 'platform/components';
import {Heading, HStack, Show, Text, VStack} from 'platform/foundation';

import type {UseFormReturn} from 'react-hook-form';

import {isNotNil} from 'ramda';

import type {CheckoutProfileError, CheckoutProfileRuleWrite} from '@dms/api/sales';
import i18n from '@dms/i18n';

import {useCheckoutRuleLookups} from '../hooks/useCheckoutRuleLookups';
import type {CheckoutRuleFormValues} from '../types/CheckoutRuleFormValues';
import {CheckoutRuleFeedback} from './CheckoutRuleFeedback';
import {CheckoutRuleImpliedDirectParent} from './CheckoutRuleImpliedDirectParent';

interface Props {
  values: CheckoutProfileRuleWrite;
  control: FormControl<CheckoutRuleFormValues>;
  formApi: UseFormReturn<CheckoutRuleFormValues>;
  errors: CheckoutProfileError[];
}

export function CheckoutRuleConditions(props: Props) {
  const allowLabels = {
    serviceCaseTypeIds: i18n.t('entity.checkout.labels.allowServiceCaseVariant'),
    serviceOrderTypeIds: i18n.t('entity.checkout.labels.allowServiceOrderType'),
    serviceOrderVariantIds: i18n.t('entity.checkout.labels.allowServiceOrderVariant'),
    authorizationProfileIds: i18n.t('entity.checkout.labels.allowAuthorizationProfile'),
    directSaleTypeIds: i18n.t('entity.checkout.labels.allowDirectSaleType'),
    directSaleVariantIds: i18n.t('entity.checkout.labels.allowDirectSaleVariant'),
  };

  const lookups = useCheckoutRuleLookups(props.values.branchId, props.values.sourceType);
  const variantField =
    props.values.sourceType === 'service-order' ? 'serviceOrderVariantIds' : 'directSaleVariantIds';

  const selectedVariants = props.values.conditions[variantField];
  const restrictVariants = isNotNil(selectedVariants);

  return (
    <VStack spacing={6}>
      <CheckoutRuleFeedback
        errors={props.errors.filter(
          (error) => !lookups.groups.some((group) => error.path.split('.')[1] === group.field)
        )}
      />
      <Show when={lookups.isError}>
        <Alert variant="error" title={i18n.t('general.notifications.changesSavingFailed')} />
      </Show>
      {lookups.groups.map((group, index) => {
        const isVariants = group.field === variantField;
        const implied = restrictVariants && !isVariants;
        const enabled = isNotNil(props.values.conditions[group.field]);
        const errors = props.errors.filter((error) => error.path.split('.')[1] === group.field);
        const impliedNames = lookups.variants
          .filter((variant) => selectedVariants?.includes(variant.value))
          .map((variant) => (index === 0 ? variant.firstTypeName : variant.secondTypeName))
          .filter(isNotNil);

        return (
          <section key={group.field} aria-label={allowLabels[group.field]}>
            <Card variant="inlineGrey">
              <HStack spacing={3} align="center" wrap>
                <Switch
                  aria-label={allowLabels[group.field]}
                  value={enabled}
                  isDisabled={
                    implied || props.formApi.formState.isSubmitting || !props.values.branchId
                  }
                  onChange={(on) => {
                    if (on && isVariants) {
                      lookups.groups
                        .filter((item) => item.field !== variantField)
                        .forEach((item) =>
                          props.formApi.setValue(`conditions.${item.field}`, null, {
                            shouldDirty: true,
                            shouldValidate: true,
                          })
                        );
                    }

                    props.formApi.setValue(`conditions.${group.field}`, on ? [] : null, {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                  }}
                />
                <Heading size={4}>{allowLabels[group.field]}</Heading>
                <Show when={implied}>
                  <Text size="small" color="secondary">
                    {i18n.t('entity.checkout.labels.impliedByVariants')}
                    {impliedNames.length > 0 ? ' ' + [...new Set(impliedNames)].join(', ') : ''}
                  </Text>
                </Show>
                <Show when={implied && props.values.sourceType === 'direct-sale'}>
                  {(selectedVariants ?? []).map((variantId) => (
                    <CheckoutRuleImpliedDirectParent
                      key={variantId}
                      variantId={variantId}
                      branchId={props.values.branchId}
                      isAuthorization={index === 0}
                      options={group.options}
                    />
                  ))}
                </Show>
              </HStack>
              <CheckoutRuleFeedback errors={errors} />
              <Show when={enabled}>
                <Separator />
                <FormField
                  control={props.control}
                  name={`conditions.${group.field}`}
                  type="multiChoice"
                  label={group.label}
                  errorMessage={errors[0]?.message}
                  options={[
                    ...group.options,
                    ...(props.values.conditions[group.field] ?? [])
                      .filter((id) => !group.options.some((option) => option.value === id))
                      .map((id) => ({value: id, label: id})),
                  ]}
                  isRequired
                  isLoading={group.isLoading}
                  isDisabled={props.formApi.formState.isSubmitting || group.isError}
                  helperText={i18n.t('entity.checkout.labels.ruleConditionValuesDescription')}
                />
              </Show>
            </Card>
          </section>
        );
      })}
    </VStack>
  );
}
